import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { workloadApi, type FaixaCarga, type CelulaHeatmap } from '../api/workload.js';
import { VazioQueEnsina } from '../componentes/transversais/VazioQueEnsina.js';
import { TimelineGrid } from '../componentes/gantt/TimelineGrid.js';
import { PainelSobrecarga } from '../componentes/gantt/PainelSobrecarga.js';
import { diasEntre } from '../componentes/gantt/timeline-utils.js';

// SPEC-UX §3.5 "Semáforo sagrado": verde/âmbar/vermelho são exclusivos de
// status DERIVADO (chip §3.4), não da faixa de carga do Workload (que é
// outro conceito — B2-D3). Usa a paleta aço/grafite para não colidir com o
// significado do semáforo.
const COR_FAIXA: Record<FaixaCarga, string> = {
  Disponivel: 'bg-borda/40 text-grafite',
  Alocado: 'bg-aco/20 text-aco-700',
  Sobrealocado: 'bg-ink text-white',
};

function inicioDaSemanaAtual(): string {
  const hoje = new Date();
  const diaSemana = hoje.getUTCDay();
  const segunda = new Date(hoje);
  segunda.setUTCDate(hoje.getUTCDate() - ((diaSemana + 6) % 7));
  return segunda.toISOString().slice(0, 10);
}
function fimDaSemana(inicio: string): string {
  const data = new Date(inicio);
  data.setUTCDate(data.getUTCDate() + 6);
  return data.toISOString().slice(0, 10);
}

function AbaLinhaDoTempo({ inicio, fim }: { inicio: string; fim: string }) {
  const [skillFiltro, setSkillFiltro] = useState<string>('');
  const { data: heatmapDados } = useQuery({
    queryKey: ['workload', 'heatmap', inicio, fim, skillFiltro],
    queryFn: () => workloadApi.heatmap(inicio, fim, skillFiltro ? [skillFiltro] : undefined),
  });
  const [recursosExpandidos, setRecursosExpandidos] = useState<Set<string>>(new Set());
  const [sobrecargaSelecionada, setSobrecargaSelecionada] = useState<{ recursoNome: string; percentual: number } | null>(null);

  if (!heatmapDados || heatmapDados.length === 0) {
    return <VazioQueEnsina explicacao="Nenhum recurso ativo com alocação nesta semana." />;
  }

  const diaPx = 22;
  const totalDias = Math.max(14, diasEntre(inicio, fim) + 1);
  const hoje = new Date().toISOString().slice(0, 10);
  const hojeIdx = hoje >= inicio ? diasEntre(inicio, hoje) : null;

  function alternarRecurso(recursoId: string) {
    setRecursosExpandidos((atual) => {
      const novo = new Set(atual);
      if (novo.has(recursoId)) novo.delete(recursoId);
      else novo.add(recursoId);
      return novo;
    });
  }

  return (
    <div className="flex flex-col gap-3">
      <input
        placeholder="Filtrar por skill…"
        value={skillFiltro}
        onChange={(e) => setSkillFiltro(e.target.value)}
        className="w-56 rounded border border-borda bg-base-100 px-2 py-1.5 text-sm text-ink"
      />

      <div className="overflow-hidden rounded-lg border border-borda bg-superficie">
        <div className="grid grid-cols-[220px_1fr]">
          <div className="border-r border-borda">
            <div className="h-10 border-b border-borda bg-borda/20" />
            {heatmapDados.map((celula) => (
              <RecursoLinha
                key={celula.recursoId}
                celula={celula}
                expandido={recursosExpandidos.has(celula.recursoId)}
                onAlternar={() => alternarRecurso(celula.recursoId)}
                onSobrecarga={() => setSobrecargaSelecionada({ recursoNome: celula.recursoNome, percentual: celula.percentual })}
              />
            ))}
          </div>

          <div className="relative overflow-x-auto">
            <div style={{ width: totalDias * diaPx, minWidth: '100%' }}>
              <div className="relative h-10 border-b border-borda bg-borda/20">
                <TimelineGrid totalDias={totalDias} diaPx={diaPx} hojeIdx={hojeIdx} dataInicio={inicio} />
              </div>
              {heatmapDados.map((celula) => (
                <RecursoTimeline
                  key={celula.recursoId}
                  recursoId={celula.recursoId}
                  expandido={recursosExpandidos.has(celula.recursoId)}
                  inicio={inicio}
                  fim={fim}
                  diaPx={diaPx}
                  totalDias={totalDias}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {sobrecargaSelecionada && (
        <PainelSobrecarga
          recursoNome={sobrecargaSelecionada.recursoNome}
          percentual={sobrecargaSelecionada.percentual}
          onFechar={() => setSobrecargaSelecionada(null)}
        />
      )}
    </div>
  );
}

function RecursoLinha({
  celula,
  expandido,
  onAlternar,
  onSobrecarga,
}: {
  celula: CelulaHeatmap;
  expandido: boolean;
  onAlternar: () => void;
  onSobrecarga: () => void;
}) {
  return (
    <button
      type="button"
      onClick={celula.faixa === 'Sobrealocado' ? onSobrecarga : onAlternar}
      className="flex h-[34px] w-full items-center justify-between gap-2 border-b border-borda px-3 text-left text-sm"
    >
      <span className="flex items-center gap-1.5 truncate">
        <span onClick={(e) => { e.stopPropagation(); onAlternar(); }} className={`inline-block cursor-pointer text-[9px] text-grafite transition-transform ${expandido ? 'rotate-90' : ''}`}>
          ▸
        </span>
        <span className="truncate text-ink">{celula.recursoNome}</span>
      </span>
      <span className={`fonte-mono rounded px-1.5 py-0.5 text-[10px] font-medium ${COR_FAIXA[celula.faixa]}`}>{celula.percentual.toFixed(0)}%</span>
    </button>
  );
}

function RecursoTimeline({ recursoId, expandido, inicio, fim, diaPx, totalDias }: { recursoId: string; expandido: boolean; inicio: string; fim: string; diaPx: number; totalDias: number }) {
  const { data: linha } = useQuery({
    queryKey: ['workload', 'linha-do-tempo', recursoId, inicio, fim],
    queryFn: () => workloadApi.linhaDoTempoRecurso(recursoId, inicio, fim),
    enabled: expandido,
  });
  const { data: ghostLoad } = useQuery({
    queryKey: ['workload', 'ghost-load', recursoId],
    queryFn: () => workloadApi.ghostLoadPorRecurso(recursoId),
    enabled: expandido,
  });

  return (
    <div className="relative border-b border-borda" style={{ height: expandido ? 34 + (linha?.barras.length ?? 0) * 26 + (ghostLoad?.length ?? 0) * 26 : 34 }}>
      <TimelineGrid totalDias={totalDias} diaPx={diaPx} hojeIdx={null} dataInicio={inicio} />
      {expandido &&
        linha?.barras.map((barra, i) => {
          const offset = diasEntre(inicio, barra.alocacoes[0]?.inicio ?? inicio);
          return (
            <div key={barra.projetoId} className="absolute h-[22px] rounded bg-aco/70" style={{ top: 34 + i * 26 + 2, left: Math.max(0, offset) * diaPx, width: 6 * diaPx }} title={barra.projetoId} />
          );
        })}
      {expandido &&
        ghostLoad?.map((g, i) => {
          const offset = diasEntre(inicio, g.inicio);
          const largura = Math.max(1, diasEntre(g.inicio, g.fim)) * diaPx;
          return (
            <div
              key={g.veredictoId}
              className="absolute h-[22px] rounded border-2 border-dashed border-aco bg-transparent"
              style={{ top: 34 + (linha?.barras.length ?? 0) * 26 + i * 26 + 2, left: Math.max(0, offset) * diaPx, width: largura }}
              title={`Demanda em avaliação — ${g.cargaHSemana}h/semana`}
            />
          );
        })}
    </div>
  );
}

// SPEC-UX §4.4 — heatmap recurso x semana como tela principal. Clique na
// célula abre as alocações que compõem a carga (fica para a tela de
// detalhe do recurso). Nenhum lugar para "editar estado do recurso": não
// existe esse dado (§4.4 nota final). Aba "Linha do tempo" (Adendo A1 §J4)
// mostra as mesmas alocações agrupadas por projeto para 1 recurso.
export function Workload() {
  const [inicio] = useState(inicioDaSemanaAtual);
  const fim = fimDaSemana(inicio);
  const [aba, setAba] = useState<'heatmap' | 'linha-do-tempo'>('heatmap');

  const { data: celulas, isLoading } = useQuery({
    queryKey: ['workload', 'heatmap', inicio, fim],
    queryFn: () => workloadApi.heatmap(inicio, fim),
  });

  return (
    <div className="flex flex-col gap-6 p-8">
      <h1 className="text-xl font-semibold text-ink">Workload</h1>
      <p className="fonte-mono text-xs text-grafite">
        Semana de {inicio} a {fim}
      </p>

      <div className="flex gap-1 border-b border-borda">
        {(['heatmap', 'linha-do-tempo'] as const).map((a) => (
          <button
            key={a}
            type="button"
            onClick={() => setAba(a)}
            className={`px-3 py-2 text-sm font-medium capitalize ${aba === a ? 'border-b-2 border-aco text-aco-700' : 'text-grafite'}`}
          >
            {a === 'heatmap' ? 'Heatmap' : 'Linha do tempo'}
          </button>
        ))}
      </div>

      {aba === 'heatmap' && (
        <>
          {isLoading && <p className="text-sm text-grafite">Carregando…</p>}

          {!isLoading && (!celulas || celulas.length === 0) && (
            <VazioQueEnsina explicacao="Nenhum recurso ativo com alocação nesta semana. O heatmap se preenche quando recursos têm alocações registradas." />
          )}

          {celulas && celulas.length > 0 && (
            <table className="w-full max-w-2xl border-collapse text-sm">
              <thead>
                <tr>
                  <th className="border-b border-borda px-3 py-2 text-left text-xs font-medium text-grafite">Recurso</th>
                  <th className="border-b border-borda px-3 py-2 text-left text-xs font-medium text-grafite">Carga</th>
                  <th className="border-b border-borda px-3 py-2 text-left text-xs font-medium text-grafite">Faixa</th>
                </tr>
              </thead>
              <tbody>
                {celulas.map((celula) => (
                  <tr key={celula.recursoId}>
                    <td className="border-b border-borda px-3 py-2 text-ink">{celula.recursoNome}</td>
                    <td className="fonte-mono border-b border-borda px-3 py-2 text-ink">{celula.percentual.toFixed(0)}%</td>
                    <td className="border-b border-borda px-3 py-2">
                      <span className={`rounded px-2 py-0.5 text-xs font-medium ${COR_FAIXA[celula.faixa]}`}>{celula.faixa}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {aba === 'linha-do-tempo' && <AbaLinhaDoTempo inicio={inicio} fim={fim} />}
    </div>
  );
}
